/**
 * Shared client library for the brand-research pipeline. Owns the single
 * `POST /workspace/:id/research` fetch, parses the SSE stream, and pushes the
 * latest progress label into a module-scoped tracker shared by every UI
 * surface (settings page, form-builder, onboarding) keyed by workspace id.
 *
 * `kickoffBrandResearch` NEVER throws - every failure path resolves with a
 * tagged `BrandResearchOutcome` and is captured to PostHog under
 * `BrandResearch.kickoff`. Callers can `void` the promise from a click handler
 * with no risk of an unhandled rejection.
 *
 * Worker contract:
 *   POST `/workspace/:id/research`
 *     Headers: `Authorization: Bearer <jwt>`, `Content-Type: application/json`
 *     Body:    `{ url?: string }` - optional override; worker falls back to
 *              the workspace's stored `website_url`.
 *     200 SSE: `data: { type: 'progress', stage, label, url? }\n\n` ending in
 *              `data: { type: 'result', ...payload }\n\n` or
 *              `data: { type: 'error', code, retryable }\n\n` (public code
 *              from `lib/generation-errors`; copy is resolved from it).
 *     200 JSON `payload` - when a completed result <60s old is cached.
 *     409 JSON `{ error: 'in_progress', startedAt }` - another client is
 *              already mid-stream for this workspace.
 *     4xx/5xx JSON `{ error, error_code? }` - auth/permission/validation
 *              failures; only a recognized `error_code` is kept.
 *
 * @module lib/brand-research
 */

import { useEffect, useState } from 'react';
import { resolveWorkerUrl } from './suggest-objectives';
import { captureException } from './telemetry/posthog/frontend';
import { isResearchErrorCode, type ResearchErrorCode } from './generation-errors';

/** Latest progress label observed for a workspace's brand-research run. */
export interface BrandResearchLabel {
  /** Workspace this label belongs to. */
  workspaceId: string;
  /** Stable public stage key for branching (e.g. `explore`). */
  stage: string;
  /** Brand-specific dynamic label rendered verbatim by the UI. */
  label: string;
  /** Optional URL associated with the stage (for tooltip / link affordance). */
  url?: string;
  /** Last update timestamp (ms epoch). */
  at: number;
}

/** Discriminated terminal outcome of `kickoffBrandResearch`. */
export type BrandResearchOutcome =
  | { kind: 'success' }
  | { kind: 'in_progress_elsewhere' }
  | { kind: 'cached'; payload: unknown }
  | { kind: 'error'; code: ResearchErrorCode };

/** Options for {@link kickoffBrandResearch}. */
export interface KickoffBrandResearchOptions {
  /** Workspace id (uuid) - both the URL path segment and the tracker key. */
  workspaceId: string;
  /** Optional URL override; worker falls back to stored `website_url`. */
  url?: string;
  /** Supabase access token; worker validates as Bearer JWT. */
  accessToken: string;
  /** Override for the global `fetch`. Used by tests. */
  fetchImpl?: typeof fetch;
  /** Override for the worker base URL. Used by tests. */
  workerUrl?: string;
  /** Optional `AbortSignal` passed through to the underlying fetch. */
  signal?: AbortSignal;
}

/** Listener signature for the label tracker. `null` means "cleared". */
type Listener = (label: BrandResearchLabel | null) => void;

const labels = new Map<string, BrandResearchLabel>();
const listeners = new Map<string, Set<Listener>>();

/**
 * Push a new label (or `null` to clear) for a workspace and synchronously
 * notify subscribed listeners.
 */
function setLabel(workspaceId: string, label: BrandResearchLabel | null): void {
  if (label === null) {
    labels.delete(workspaceId);
  } else {
    labels.set(workspaceId, label);
  }
  const set = listeners.get(workspaceId);
  if (!set) return;
  for (const fn of set) {
    try {
      fn(label);
    } catch (err) {
      // A listener throwing must not break the chain or the SSE consumer.
      captureException(
        err instanceof Error ? err : new Error(String(err)),
        'BrandResearch.listenerThrow',
        { workspaceId },
      );
    }
  }
}

/**
 * Subscribe to label changes for a workspace. Listener fires on every update
 * (including `null` clears on terminal events).
 *
 * @returns Unsubscribe function (idempotent; safe to call after unmount).
 */
export function subscribeBrandResearchLabel(
  workspaceId: string,
  listener: Listener,
): () => void {
  let set = listeners.get(workspaceId);
  if (!set) {
    set = new Set<Listener>();
    listeners.set(workspaceId, set);
  }
  set.add(listener);
  return () => {
    const s = listeners.get(workspaceId);
    if (!s) return;
    s.delete(listener);
    if (s.size === 0) listeners.delete(workspaceId);
  };
}

/** Synchronous read of the latest label for a workspace, or `null`. */
export function getBrandResearchLabel(
  workspaceId: string,
): BrandResearchLabel | null {
  return labels.get(workspaceId) ?? null;
}

/**
 * React hook that re-renders whenever the label for `workspaceId` changes.
 *
 * @param workspaceId Workspace to watch. `null`/`undefined` returns null.
 */
export function useBrandResearchLabel(
  workspaceId: string | null | undefined,
): BrandResearchLabel | null {
  const [label, setStateLabel] = useState<BrandResearchLabel | null>(() =>
    workspaceId ? getBrandResearchLabel(workspaceId) : null,
  );

  useEffect(() => {
    if (!workspaceId) {
      setStateLabel(null);
      return undefined;
    }
    // Sync to current value on (re-)subscribe - guards against missed
    // updates between render and effect.
    setStateLabel(getBrandResearchLabel(workspaceId));
    return subscribeBrandResearchLabel(workspaceId, (next) => {
      setStateLabel(next);
    });
  }, [workspaceId]);

  return label;
}

function isProgress(msg: unknown): msg is {
  type: 'progress';
  stage: string;
  label: string;
  url?: string;
} {
  if (!msg || typeof msg !== 'object') return false;
  const m = msg as Record<string, unknown>;
  return (
    m.type === 'progress' &&
    typeof m.stage === 'string' &&
    typeof m.label === 'string'
  );
}

function isResult(msg: unknown): msg is { type: 'result'; [k: string]: unknown } {
  if (!msg || typeof msg !== 'object') return false;
  return (msg as { type?: unknown }).type === 'result';
}

function isErrorEvent(
  msg: unknown,
): msg is { type: 'error'; code?: unknown } {
  if (!msg || typeof msg !== 'object') return false;
  return (msg as { type?: unknown }).type === 'error';
}

/**
 * Parse a single SSE event block (up to `\n\n`). Joins multi-line `data:`
 * fields with `\n` per spec. Returns `null` for missing `data:` or invalid
 * JSON.
 */
function parseSseBlock(block: string): unknown | null {
  const lines = block.split('\n');
  const dataLines: string[] = [];
  for (const line of lines) {
    if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).replace(/^\s/, ''));
    }
  }
  if (dataLines.length === 0) return null;
  const raw = dataLines.join('\n');
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * Consume a `text/event-stream` response into the tracker, resolving when a
 * terminal `result`/`error` event arrives or the stream closes. A clean close
 * with no terminal event resolves `{ kind: 'success' }` - the worker persists
 * the row independently of the client connection.
 */
async function consumeSseStream(
  workspaceId: string,
  body: ReadableStream<Uint8Array>,
): Promise<BrandResearchOutcome> {
  const reader = body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  let outcome: BrandResearchOutcome = { kind: 'success' };

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // SSE events are separated by `\n\n`; drain every complete event.
      let boundary = buffer.indexOf('\n\n');
      while (boundary !== -1) {
        const block = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        const msg = parseSseBlock(block);

        if (isProgress(msg)) {
          setLabel(workspaceId, {
            workspaceId,
            stage: msg.stage,
            label: msg.label,
            url: msg.url,
            at: Date.now(),
          });
        } else if (isResult(msg)) {
          setLabel(workspaceId, null);
          outcome = { kind: 'success' };
          return outcome;
        } else if (isErrorEvent(msg)) {
          setLabel(workspaceId, null);
          if (isResearchErrorCode(msg.code)) {
            // The worker classified and captured this failure already.
            outcome = { kind: 'error', code: msg.code };
            return outcome;
          }
          // Unrecognized event: a contract failure. Its text is never kept.
          captureException(
            new Error('Brand research stream sent an unrecognized error event'),
            'BrandResearch.kickoff',
            { workspaceId, stage: 'sse_error' },
          );
          outcome = { kind: 'error', code: 'failed' };
          return outcome;
        }
        // Unknown event types are ignored - forward-compat.
        boundary = buffer.indexOf('\n\n');
      }
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {
      // releaseLock can throw when already released; harmless.
    }
  }

  return outcome;
}

/**
 * Fire the brand-research pipeline for a workspace and consume the resulting
 * SSE stream into the shared label tracker. Always resolves - never rejects.
 * Errors are captured to PostHog under `BrandResearch.kickoff` and surfaced
 * as `{ kind: 'error' }`.
 */
export async function kickoffBrandResearch(
  opts: KickoffBrandResearchOptions,
): Promise<BrandResearchOutcome> {
  const {
    workspaceId,
    url,
    accessToken,
    fetchImpl,
    workerUrl,
    signal,
  } = opts;
  const fetcher = fetchImpl ?? fetch;
  const baseUrl = (workerUrl ?? resolveWorkerUrl()).replace(/\/+$/, '');

  try {
    const body: Record<string, string> = {};
    if (url) body.url = url;

    const response = await fetcher(
      `${baseUrl}/workspace/${workspaceId}/research`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
        signal,
      },
    );

    // 409 - another client is already streaming. Don't clobber the existing
    // label; the in-progress run will keep updating it.
    if (response.status === 409) {
      return { kind: 'in_progress_elsewhere' };
    }

    if (!response.ok) {
      let code: ResearchErrorCode = 'failed';
      try {
        const json = (await response.json()) as { error_code?: unknown };
        if (isResearchErrorCode(json.error_code)) code = json.error_code;
      } catch {
        // Keep the generic code.
      }
      captureException(
        new Error(`Brand research request failed: HTTP ${response.status}`),
        'BrandResearch.kickoff',
        { workspaceId, status: response.status, code },
      );
      return { kind: 'error', code };
    }

    const contentType = response.headers.get('Content-Type') ?? '';

    if (contentType.includes('application/json')) {
      const payload = (await response.json()) as unknown;
      return { kind: 'cached', payload };
    }

    if (contentType.includes('text/event-stream') && response.body) {
      return await consumeSseStream(workspaceId, response.body);
    }

    // Unknown content type - soft success so the tracker doesn't get stuck.
    // The worker has persisted the row regardless.
    return { kind: 'success' };
  } catch (err) {
    setLabel(workspaceId, null);
    captureException(
      err instanceof Error ? err : new Error(String(err)),
      'BrandResearch.kickoff',
      { workspaceId, code: 'failed' },
    );
    return { kind: 'error', code: 'failed' };
  }
}

/**
 * Test-only utility for resetting in-memory tracker state between vitest
 * cases. Namespaced so production callers can't accidentally clear listeners
 * mid-stream.
 */
export const __test = {
  reset(): void {
    labels.clear();
    listeners.clear();
  },
};
